// @ts-nocheck
/**
 * Creator Dashboard Component
 * Simplified interface for content creators with IBM Carbon Design System
 */

import React, { useState, useEffect } from 'react';
import {
  Tile,
  ClickableTile,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  ContentSwitcher,
  Switch,
  ProgressIndicator,
  ProgressStep,
  SkeletonText,
  InlineNotification,
  Modal,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Stack,
  Grid,
  Column,
} from '@carbon/react';
import {
  Add,
  Document,
  Template,
  Analytics,
  Send,
  Edit,
  View,
  Time,
  CheckmarkFilled,
  WarningAlt,
  Calendar,
} from '@carbon/icons-react';
import { useNavigate } from 'react-router-dom';
import styles from './CreatorDashboard.module.scss';

interface ContentItem {
  id: string;
  title: string;
  type: 'blog' | 'article' | 'tutorial' | 'guide';
  status: 'draft' | 'pending_review' | 'approved' | 'published';
  createdAt: Date;
  lastModified: Date;
  views: number;
  engagement: number;
  template?: string;
}

interface ContentTemplate {
  id: string;
  name: string;
  type: 'blog' | 'article' | 'tutorial' | 'guide';
  description: string;
  icon: React.ComponentType;
  structure: {
    sections: string[];
    wordCount: number;
    readingTime: number;
  };
}

interface PerformanceMetrics {
  totalViews: number;
  totalEngagement: number;
  publishedContent: number;
  pendingReviews: number;
  averageReadTime: number;
  topPerformingContent: ContentItem[];
}

const CreatorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [selectedView, setSelectedView] = useState<'overview' | 'content' | 'performance'>('overview');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ContentTemplate | null>(null);
  const [quickDraftModal, setQuickDraftModal] = useState(false);
  const [quickDraft, setQuickDraft] = useState({ title: '', type: 'blog', description: '' });

  const templates: ContentTemplate[] = [
    {
      id: 'blog-standard',
      name: 'Standard Blog Post',
      type: 'blog',
      description: 'Perfect for regular blog updates and announcements',
      icon: Document,
      structure: {
        sections: ['Introduction', 'Main Content', 'Conclusion', 'Call to Action'],
        wordCount: 800,
        readingTime: 4,
      },
    },
    {
      id: 'tutorial-step',
      name: 'Step-by-Step Tutorial',
      type: 'tutorial',
      description: 'Ideal for educational content with clear instructions',
      icon: Template,
      structure: {
        sections: ['Overview', 'Prerequisites', 'Steps', 'Summary', 'Resources'],
        wordCount: 1200,
        readingTime: 6,
      },
    },
    {
      id: 'article-research',
      name: 'Research Article',
      type: 'article',
      description: 'In-depth articles with research and citations',
      icon: Analytics,
      structure: {
        sections: ['Abstract', 'Introduction', 'Methodology', 'Results', 'Discussion', 'References'],
        wordCount: 2000,
        readingTime: 10,
      },
    },
    {
      id: 'guide-comprehensive',
      name: 'Comprehensive Guide',
      type: 'guide',
      description: 'Complete guides covering topics thoroughly',
      icon: Document,
      structure: {
        sections: ['Introduction', 'Chapters', 'Examples', 'Best Practices', 'FAQ', 'Resources'],
        wordCount: 3000,
        readingTime: 15,
      },
    },
  ];

  useEffect(() => {
    loadCreatorData();
  }, []);

  const loadCreatorData = async () => {
    setLoading(true);
    try {
      // Load content items
      const contentResponse = await fetch('/api/v1/creator/content', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (contentResponse.ok) {
        const contentData = await contentResponse.json();
        setContentItems(contentData.items || []);
      }

      // Load performance metrics
      const metricsResponse = await fetch('/api/v1/creator/metrics', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (metricsResponse.ok) {
        const metricsData = await metricsResponse.json();
        setMetrics(metricsData.metrics);
      }
    } catch (error) {
      console.error('Failed to load creator data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContent = (template?: ContentTemplate) => {
    if (template) {
      navigate(`/creator/editor/new?template=${template.id}`);
    } else {
      navigate('/creator/editor/new');
    }
  };

  const handleQuickSubmit = async () => {
    try {
      const response = await fetch('/api/v1/creator/content/quick-draft', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quickDraft),
      });

      if (response.ok) {
        const result = await response.json();
        navigate(`/creator/editor/${result.id}`);
      }
    } catch (error) {
      console.error('Failed to create quick draft:', error);
    }
  };

  const handleSubmitForApproval = async (contentId: string) => {
    try {
      const response = await fetch(`/api/v1/creator/content/${contentId}/submit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (response.ok) {
        await loadCreatorData();
      }
    } catch (error) {
      console.error('Failed to submit for approval:', error);
    }
  };

  const getStatusTag = (status: ContentItem['status']) => {
    const statusConfig = {
      draft: { type: 'gray' as const, icon: Edit },
      pending_review: { type: 'purple' as const, icon: Time },
      approved: { type: 'green' as const, icon: CheckmarkFilled },
      published: { type: 'blue' as const, icon: View },
    };

    const config = statusConfig[status];
    return (
      <Tag type={config.type} renderIcon={config.icon}>
        {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Tag>
    );
  };

  const contentTableHeaders = [
    { key: 'title', header: 'Title' },
    { key: 'type', header: 'Type' },
    { key: 'status', header: 'Status' },
    { key: 'views', header: 'Views' },
    { key: 'lastModified', header: 'Last Modified' },
    { key: 'actions', header: 'Actions' },
  ];

  const contentTableRows = contentItems.map(item => ({
    id: item.id,
    title: item.title,
    type: item.type,
    status: getStatusTag(item.status),
    views: item.views.toLocaleString(),
    lastModified: new Date(item.lastModified).toLocaleDateString(),
    actions: (
      <div className={styles.actionButtons}>
        <Button kind="ghost" size="sm" renderIcon={Edit} onClick={() => navigate(`/creator/editor/${item.id}`)}>
          Edit
        </Button>
        {item.status === 'draft' && (
          <Button kind="tertiary" size="sm" renderIcon={Send} onClick={() => handleSubmitForApproval(item.id)}>
            Submit
          </Button>
        )}
      </div>
    ),
  }));

  if (loading) {
    return (
      <div className={styles.creatorDashboard}>
        <SkeletonText paragraph lineCount={8} />
      </div>
    );
  }

  return (
    <div className={styles.creatorDashboard}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            <h1>Content Creator Dashboard</h1>
            <p>Create, manage, and track your content performance</p>
          </div>
          <div className={styles.headerActions}>
            <Button kind="tertiary" renderIcon={Template} onClick={() => setShowTemplateModal(true)}>
              Browse Templates
            </Button>
            <Button kind="primary" renderIcon={Add} onClick={() => setQuickDraftModal(true)}>
              Create New Content
            </Button>
          </div>
        </div>
      </div>

      <ContentSwitcher
        onChange={e => setSelectedView(e.name as any)}
        selectedIndex={['overview', 'content', 'performance'].indexOf(selectedView)}
      >
        <Switch name="overview" text="Overview" />
        <Switch name="content" text="My Content" />
        <Switch name="performance" text="Performance" />
      </ContentSwitcher>

      {selectedView === 'overview' && (
        <Grid className={styles.overviewGrid}>
          <Column lg={8} md={4} sm={4}>
            <Stack gap={6}>
              <div className={styles.quickActions}>
                <h3>Quick Actions</h3>
                <div className={styles.templateGrid}>
                  {templates.map(template => (
                    <ClickableTile
                      key={template.id}
                      onClick={() => handleCreateContent(template)}
                      className={styles.templateTile}
                    >
                      <template.icon size={32} />
                      <h4>{template.name}</h4>
                      <p>{template.description}</p>
                      <Tag type="outline" size="sm">
                        ~{template.structure.readingTime} min read
                      </Tag>
                    </ClickableTile>
                  ))}
                </div>
              </div>

              <div className={styles.recentContent}>
                <h3>Recent Content</h3>
                {contentItems.slice(0, 5).map(item => (
                  <Tile key={item.id} className={styles.contentTile}>
                    <div className={styles.contentHeader}>
                      <h4>{item.title}</h4>
                      {getStatusTag(item.status)}
                    </div>
                    <div className={styles.contentMeta}>
                      <span>
                        <View /> {item.views}
                      </span>
                      <span>
                        <Calendar /> {new Date(item.lastModified).toLocaleDateString()}
                      </span>
                    </div>
                  </Tile>
                ))}
              </div>
            </Stack>
          </Column>

          <Column lg={8} md={4} sm={4}>
            {metrics && (
              <Stack gap={6}>
                <div className={styles.metricsOverview}>
                  <h3>Performance Snapshot</h3>
                  <div className={styles.metricsTiles}>
                    <Tile>
                      <div className={styles.metricValue}>{metrics.totalViews.toLocaleString()}</div>
                      <div className={styles.metricLabel}>Total Views</div>
                    </Tile>
                    <Tile>
                      <div className={styles.metricValue}>{metrics.publishedContent}</div>
                      <div className={styles.metricLabel}>Published</div>
                    </Tile>
                    <Tile>
                      <div className={styles.metricValue}>{metrics.pendingReviews}</div>
                      <div className={styles.metricLabel}>Pending Review</div>
                    </Tile>
                    <Tile>
                      <div className={styles.metricValue}>{metrics.averageReadTime}min</div>
                      <div className={styles.metricLabel}>Avg. Read Time</div>
                    </Tile>
                  </div>
                </div>

                <div className={styles.topPerforming}>
                  <h3>Top Performing Content</h3>
                  {metrics.topPerformingContent.map((item, index) => (
                    <div key={item.id} className={styles.performanceItem}>
                      <span className={styles.rank}>#{index + 1}</span>
                      <div className={styles.itemDetails}>
                        <strong>{item.title}</strong>
                        <div className={styles.itemStats}>
                          <span>{item.views} views</span>
                          <span>{item.engagement}% engagement</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Stack>
            )}
          </Column>
        </Grid>
      )}

      {selectedView === 'content' && (
        <div className={styles.contentView}>
          <DataTable rows={contentTableRows} headers={contentTableHeaders} isSortable>
            {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
              <TableContainer>
                <Table {...getTableProps()}>
                  <TableHead>
                    <TableRow>
                      {headers.map(header => (
                        <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(row => (
                      <TableRow {...getRowProps({ row })}>
                        {row.cells.map(cell => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        </div>
      )}

      {selectedView === 'performance' && metrics && (
        <div className={styles.performanceView}>
          <InlineNotification
            kind="info"
            title="Performance Analytics"
            subtitle="Track how your content is performing and identify opportunities for improvement"
            hideCloseButton
          />
          {/* Performance charts would go here */}
        </div>
      )}

      {/* Template Selection Modal */}
      <Modal
        open={showTemplateModal}
        onRequestClose={() => setShowTemplateModal(false)}
        modalHeading="Choose a Content Template"
        primaryButtonText="Create"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => selectedTemplate && handleCreateContent(selectedTemplate)}
        onSecondarySubmit={() => setShowTemplateModal(false)}
      >
        <div className={styles.templateSelection}>
          {templates.map(template => (
            <ClickableTile
              key={template.id}
              onClick={() => setSelectedTemplate(template)}
              className={selectedTemplate?.id === template.id ? styles.selected : ''}
            >
              <h4>{template.name}</h4>
              <p>{template.description}</p>
              <div className={styles.templateMeta}>
                <Tag type="gray">~{template.structure.wordCount} words</Tag>
                <Tag type="gray">{template.structure.readingTime} min read</Tag>
              </div>
            </ClickableTile>
          ))}
        </div>
      </Modal>

      {/* Quick Draft Modal */}
      <Modal
        open={quickDraftModal}
        onRequestClose={() => setQuickDraftModal(false)}
        modalHeading="Quick Draft"
        primaryButtonText="Create Draft"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleQuickSubmit}
        onSecondarySubmit={() => setQuickDraftModal(false)}
      >
        <Stack gap={6}>
          <TextInput
            id="draft-title"
            labelText="Content Title"
            placeholder="Enter a title for your content"
            value={quickDraft.title}
            onChange={e => setQuickDraft({ ...quickDraft, title: e.target.value })}
          />
          <Select
            id="draft-type"
            labelText="Content Type"
            value={quickDraft.type}
            onChange={e => setQuickDraft({ ...quickDraft, type: e.target.value as any })}
          >
            <SelectItem value="blog" text="Blog Post" />
            <SelectItem value="article" text="Article" />
            <SelectItem value="tutorial" text="Tutorial" />
            <SelectItem value="guide" text="Guide" />
          </Select>
          <TextArea
            id="draft-description"
            labelText="Brief Description"
            placeholder="Describe what this content will cover"
            value={quickDraft.description}
            onChange={e => setQuickDraft({ ...quickDraft, description: e.target.value })}
            rows={4}
          />
        </Stack>
      </Modal>
    </div>
  );
};

export default CreatorDashboard;
